import { all, put, takeLatest, call, select } from 'redux-saga/effects';
import {
  AddMemberActionTypes,
  OrganisationActionTypes,
  EditMemberActionTypes
} from './types';
import {
  fetchAddMemberFailure,
  fetchAddMemberSuccess,
  updateOrgProfileFailure,
  updateOrgProfileSuccess,
  fetchOrgProfileFailure,
  fetchOrgProfileSuccess,
  fetchOrgTenantsSuccess,
  fetchOrgTenantsFailure,
  setAddMemberModelOpenStart,
  updateNewRecordAddedFlag,
  setEditMemberInit,
  setEditMemberSuccess,
  setEditMemberFailure,
  setEditMemberModelOpenStart,
  updateOrgProfileUpdateFlag
} from './action';
import Services from 'services';
import { fetchMemberStart } from 'redux/members/actions';
import { fetchAppFailure } from 'redux/app/actions';
import util from 'utilities/helper.util';
import errorJson from 'locales/en.json';

function* doAddNewMember(request: any) {
  try {
    const response = yield call(
      Services.member.addNewMember,
      request.payload.member
    );
    if (response.error) {
      let localisedErrorMsg = util.optimizeErrMsg(
        errorJson.members.errors,
        'members.errors',
        response.error
      );
      localisedErrorMsg =
        localisedErrorMsg === 'Unknown error'
          ? 'Add new member failed'
          : localisedErrorMsg;
      yield put(fetchAddMemberFailure(localisedErrorMsg));
    } else {
      yield put(updateNewRecordAddedFlag(true));
      try {
        const response = yield put(fetchMemberStart(request.payload.params));
        if (response.error) {
          let localisedErrorMsg = util.optimizeErrMsg(
            errorJson.members.errors,
            'common.errors',
            response.error
          );
          localisedErrorMsg =
            localisedErrorMsg === 'Unknown error'
              ? 'Fetch new member failed'
              : localisedErrorMsg;
          yield put(fetchAddMemberFailure(localisedErrorMsg));
        } else {
          yield put(fetchAddMemberSuccess(response));
        }
      } catch (error) {
        yield put(fetchAddMemberFailure(util.UNKNOWN_ERROR)); // Unknown error
      }
    }
  } catch (error) {
    let localisedErrorMsg = util.optimizeErrMsg(
      errorJson.members.errors,
      'common.errors',
      error
    );
    localisedErrorMsg =
      localisedErrorMsg === 'Unknown error'
        ? 'Add new member failed'
        : localisedErrorMsg;
    yield put(fetchAddMemberFailure(localisedErrorMsg)); // Unknown error
  } finally {
    yield put(setAddMemberModelOpenStart(false));
  }
}

function* doUpdateOrganisationProfile(request: any) {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  if (session.isAuthenticated) {
    try {
      const response = yield call(
        Services.member.updateOrganisationProfile,
        request.payload
      );
      if (!response.error) {
        yield put(updateOrgProfileSuccess(response));
        if (request.payload.refreshMembersList) {
          yield put(fetchMemberStart(request.payload.params));
        }
      } else {
        yield put(updateOrgProfileFailure(response.error.message));
      }
    } catch (error) {
      const response = {
        error: {
          message: 'Internal Server Error.',
          type: 'unique violation',
          code: 401
        }
      };
      yield put(updateOrgProfileFailure(response.error.message));
      yield put(fetchAppFailure(response.error.message));
    }
  }
}

export function* doSetAddMemberModalOpen(value: any) {
  if (value.payload) {
    yield put(updateOrgProfileUpdateFlag(false));
  }
}

function* onSetAddMemberModalOpen() {
  yield takeLatest(
    AddMemberActionTypes.OPEN_ADD_MEMBER_MODAL_START,
    doSetAddMemberModalOpen
  );
}

function* doFetchOrganisationProfile(request: any) {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  if (session.isAuthenticated) {
    try {
      const response = yield call(
        Services.member.fetchOrganisationProfile,
        request.payload
      );
      if (!response.error) {
        yield put(fetchOrgProfileSuccess(response));
      } else {
        yield put(fetchOrgProfileFailure(response.error.message));
      }
    } catch (error) {
      const response = {
        error: {
          message: 'Internal Server Error.',
          type: 'unique violation',
          code: 401
        }
      };
      yield put(fetchOrgProfileFailure(response.error.message));
    }
  }
}

function* doFetchOrganisationTenants() {
  try {
    const response = yield call(Services.member.fetchOrganisationTenants);
    if (!response.error) {
      if (Array.isArray(response.Tenants)) {
        yield put(fetchOrgTenantsSuccess(response.Tenants));
      } else {
        yield put(fetchOrgTenantsFailure('Unable to fetch tenants'));
      }
    } else {
      yield put(fetchOrgTenantsFailure(response.error.message));
    }
  } catch (error) {
    const response = {
      error: {
        message: 'Internal Server Error.',
        type: 'unique violation',
        code: 401
      }
    };
    yield put(fetchOrgTenantsFailure(response.error.message));
  }
}

function* onAddNewMember() {
  yield takeLatest(
    AddMemberActionTypes.FETCH_ADD_MEMBER_REQUEST,
    doAddNewMember
  );
}

export function* onUpdateOrganisationProfile() {
  yield takeLatest(
    OrganisationActionTypes.UPDATE_ORG_PROFILE_START,
    doUpdateOrganisationProfile
  );
}

export function* onFetchOrganisationProfile() {
  yield takeLatest(
    OrganisationActionTypes.FETCH_ORG_PROFILE_START,
    doFetchOrganisationProfile
  );
}

export function* onFetchOrganisationTenants() {
  yield takeLatest(
    OrganisationActionTypes.FETCH_ORG_TENANTS_START,
    doFetchOrganisationTenants
  );
}

export function* doRemoveNewRecordAddedFlag() {}

function* onUpdateNewRecordAddedFlag() {
  yield takeLatest(
    AddMemberActionTypes.UPDATE_NEW_RECORD_ADDED_FLAG,
    doRemoveNewRecordAddedFlag
  );
}

export function* doSetEditMemberModalOpen(value: any) {
  if (!value.payload) {
    yield put(setEditMemberInit({ type: 'ADD', data: undefined }));
  } else {
    yield put(updateOrgProfileUpdateFlag(false));
  }
}

function* onSetEditMemberModalOpen() {
  yield takeLatest(
    EditMemberActionTypes.OPEN_EDIT_MEMBER_MODAL_START,
    doSetEditMemberModalOpen
  );
}

export function* doSetEditMemberRequest(request: any) {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  if (session.isAuthenticated) {
    try {
      const response = yield call(
        Services.member.updateOrganisationProfile,
        request.payload
      );

      if (!response.error) {
        yield put(setEditMemberSuccess());
        yield put(fetchMemberStart(request.payload.params));
      } else {
        yield put(setEditMemberFailure(response.error.message));
      }

      yield put(setEditMemberModelOpenStart(false));
    } catch (error) {
      const response = {
        error: {
          message: 'Internal Server Error.',
          type: 'unique violation',
          code: 401
        }
      };
      yield put(setEditMemberFailure(response.error.message));
      yield put(fetchAppFailure(response.error.message));
      yield put(setEditMemberModelOpenStart(false));
    }
  }
}

function* onSetEditMemberRequest() {
  yield takeLatest(
    EditMemberActionTypes.FETCH_EDIT_MEMBER_REQUEST,
    doSetEditMemberRequest
  );
}

function* MemberSagas() {
  yield all([
    call(onAddNewMember),
    call(onUpdateOrganisationProfile),
    call(onFetchOrganisationProfile),
    call(onFetchOrganisationTenants),
    call(onSetAddMemberModalOpen),
    call(onUpdateNewRecordAddedFlag),
    call(onSetEditMemberModalOpen),
    call(onSetEditMemberRequest)
  ]);
}
export default MemberSagas;
